"use client";

import { Image } from "@/components/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LazyMotion, domAnimation } from "@/lib/framer";
import { cn } from "@/lib/utils";
import { Link } from "@inertiajs/react";
import { format, formatDistanceToNow, subDays, subMinutes } from "date-fns";
import {
  ArrowRight,
  CheckIcon,
  GitCommitIcon,
  GitPullRequestIcon,
} from "lucide-react";
import { Container } from "./container";
import ArrowBottom from "./ui/arrow-bottom";

function DemoCallout({
  children,
  label,
  side = "top",
}: {
  children: React.ReactNode;
  label: string;
  side?: "top" | "right";
}) {
  return (
    <div className="relative overflow-visible">
      {children}
      <div
        aria-hidden
        className={cn(
          "border-golden-500 bg-popover text-popover-foreground pointer-events-none absolute z-10 rounded-lg border px-3 py-1.5 text-xs shadow-lg select-none",
          side === "top" && "bottom-full left-1/2 mb-2 -translate-x-1/2",
          side === "right" && "top-1/2 left-full ml-2 -translate-y-1/2",
        )}
      >
        <p className="text-golden-500 whitespace-nowrap">{label}</p>
        {/* Outer golden triangle + inner popover fill so the arrow matches the bordered bubble */}
        <span
          aria-hidden
          className={cn(
            "absolute",
            side === "top" &&
              "top-full left-1/2 -translate-x-1/2 border-x-[6px] border-t-[7px] border-x-transparent border-t-golden-500",
            side === "right" &&
              "top-1/2 right-full -translate-y-1/2 border-y-[6px] border-r-[7px] border-y-transparent border-r-golden-500",
          )}
        />
        <span
          aria-hidden
          className={cn(
            "absolute",
            side === "top" &&
              "top-full left-1/2 -translate-x-1/2 border-x-[5px] border-t-[6px] border-x-transparent border-t-popover",
            side === "right" &&
              "top-1/2 right-full translate-x-px -translate-y-1/2 border-y-[5px] border-r-[6px] border-y-transparent border-r-popover",
          )}
        />
      </div>
    </div>
  );
}

function Changelog() {
  const now = new Date();
  const tenMinutesAgo = subMinutes(now, 10);
  const twoDaysAgo = subDays(now, 2);
  const fiveDaysAgo = subDays(now, 5);
  const sevenDaysAgo = subDays(now, 7);

  return (
    <div className="bg-card text-card-foreground mx-auto max-w-2xl cursor-default overflow-visible rounded-lg border p-6 shadow-lg select-none">
      <div className="space-y-6 overflow-visible">
        <div className="bg-muted overflow-visible rounded-lg p-4 shadow-xs">
          <div className="mb-2 flex items-start justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="flex items-center gap-1 rounded-full border-transparent bg-blue-500/10 px-2 py-1 text-xs text-blue-500 opacity-50"
              >
                🚀 Feature
              </Badge>
            </div>
            <div
              className="text-muted-foreground mt-0 flex-shrink-0 text-right text-xs whitespace-nowrap opacity-50"
              title={format(tenMinutesAgo, "PPpp")}
              suppressHydrationWarning
            >
              {formatDistanceToNow(tenMinutesAgo, { addSuffix: true })}
            </div>
          </div>
          <DemoCallout label="AI-powered code summary">
            <pre className="text-foreground mb-2 text-sm whitespace-pre-wrap">
              Developed AI-powered chatbot for customer support to reduce
              response times and improve user satisfaction scores.
            </pre>
          </DemoCallout>
          <div className="text-muted-foreground flex flex-col gap-2 text-xs opacity-50 md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-start">
                <Image
                  src="https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Alex Johnson"
                  className="mr-1 size-4 rounded-full"
                  width={16}
                  height={16}
                />
                Alex Johnson
              </div>
              <Badge variant="info" className="font-mono shadow-xs">
                main
              </Badge>
              <div className="flex items-start">
                <GitCommitIcon className="mr-1 size-4" />
                <span className="line-clamp-2 max-w-xs">
                  Integrate AI chatbot for customer support (#4215)
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-mono text-green-700" title="Lines Added">
                +1823
              </span>
              <span className="font-mono text-red-600" title="Lines Removed">
                -42
              </span>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4 shadow-xs">
          <div className="mb-2 flex items-start justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="flex items-center gap-1 rounded-full border-transparent bg-blue-500/10 px-2 py-1 text-xs text-blue-500"
              >
                🚀 Feature
              </Badge>
              <DemoCallout label="Auto-categorized" side="right">
                <Badge
                  variant="outline"
                  className="flex cursor-default items-center gap-1 rounded-full border-transparent bg-green-500/10 px-2 py-1 text-xs text-green-500 select-none"
                >
                  ⚡️ Improvement
                </Badge>
              </DemoCallout>
            </div>
            <div
              className="text-muted-foreground mt-0 flex-shrink-0 text-right text-xs whitespace-nowrap opacity-50"
              title={format(twoDaysAgo, "PPpp")}
              suppressHydrationWarning
            >
              {formatDistanceToNow(twoDaysAgo, { addSuffix: true })}
            </div>
          </div>
          <pre className="text-muted-foreground mb-2 text-sm whitespace-pre-wrap opacity-50">
            Introduced multi-factor authentication system to enhance account
            security and meet compliance requirements for enterprise clients.
          </pre>
          <div className="text-muted-foreground flex flex-col gap-2 text-xs opacity-50 md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-start">
                <Image
                  src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Sarah Williams"
                  className="mr-1 size-4 rounded-full"
                  width={16}
                  height={16}
                />
                Sarah Williams
              </div>
              <Badge variant="info" className="font-mono shadow-xs">
                feat-2fa
              </Badge>
              <div className="flex items-start">
                <GitCommitIcon className="mr-1 size-4" />
                <span className="line-clamp-2 max-w-xs">
                  Implement multi-factor authentication (#4230)
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-mono text-green-700" title="Lines Added">
                +756
              </span>
              <span className="font-mono text-red-600" title="Lines Removed">
                -12
              </span>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4 shadow-xs">
          <div className="mb-2 flex items-start justify-between gap-4 opacity-30">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="flex items-center gap-1 rounded-full border-transparent bg-red-500/10 px-2 py-1 text-xs text-red-500"
              >
                🐛 Bugfix
              </Badge>
            </div>
            <div
              className="text-muted-foreground mt-0 flex-shrink-0 text-right text-xs whitespace-nowrap"
              title={format(fiveDaysAgo, "PPpp")}
              suppressHydrationWarning
            >
              {formatDistanceToNow(fiveDaysAgo, { addSuffix: true })}
            </div>
          </div>
          <pre className="text-muted-foreground mb-2 text-sm whitespace-pre-wrap opacity-50">
            Fixed critical issue in payment processing module that was causing
            occasional double-charging of customer accounts.
          </pre>
          <div className="text-muted-foreground flex flex-col gap-2 text-xs md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-start">
                <DemoCallout label="See who made this change" side="right">
                  <div className="flex items-center">
                    <Image
                      src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      alt="Lisa Thompson"
                      className="mr-1 size-4 rounded-full"
                      width={16}
                      height={16}
                    />
                    Lisa Thompson
                  </div>
                </DemoCallout>
              </div>
              <Badge variant="info" className="font-mono shadow-xs">
                fix-double-charge
              </Badge>
              <div className="flex items-start opacity-50">
                <GitCommitIcon className="mr-1 size-4" />
                <span className="line-clamp-2 max-w-xs">
                  Fix double-charging bug in payment processing (#4250)
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2 opacity-50">
              <span className="font-mono text-green-700" title="Lines Added">
                +67
              </span>
              <span className="font-mono text-red-600" title="Lines Removed">
                -23
              </span>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4 shadow-xs">
          <div className="mb-2 flex items-start justify-between gap-4 opacity-30">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="flex items-center gap-1 rounded-full border-transparent bg-purple-500/10 px-2 py-1 text-xs text-purple-500"
              >
                🔧 Refactoring
              </Badge>
              <Badge
                variant="outline"
                className="flex items-center gap-1 rounded-full border-transparent bg-green-500/10 px-2 py-1 text-xs text-green-500"
              >
                ⚡️ Improvement
              </Badge>
            </div>
            <div
              className="text-muted-foreground mt-0 flex-shrink-0 text-right text-xs whitespace-nowrap"
              title={format(sevenDaysAgo, "PPpp")}
              suppressHydrationWarning
            >
              {formatDistanceToNow(sevenDaysAgo, { addSuffix: true })}
            </div>
          </div>
          <pre className="text-muted-foreground mb-2 text-sm whitespace-pre-wrap opacity-30">
            Refactored authentication module to use JWT tokens, improving
            scalability and reducing server load. This change simplifies the
            codebase and enhances security.
          </pre>
          <div className="text-muted-foreground flex flex-col gap-2 text-xs md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-start opacity-30">
                <Image
                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Emily Chen"
                  className="mr-1 size-4 rounded-full"
                  width={16}
                  height={16}
                />
                Emily Chen
              </div>
              <Badge variant="info" className="font-mono opacity-30 shadow-xs">
                refactor-auth
              </Badge>
              <DemoCallout label="Dive into the code">
                <div className="flex items-start">
                  <GitPullRequestIcon className="mr-1 size-4" />
                  <span className="line-clamp-2 max-w-xs">
                    Refactor auth system to use JWT (#4260)
                  </span>
                </div>
              </DemoCallout>
            </div>
            <div className="flex items-start gap-2 opacity-30">
              <span className="font-mono text-green-700" title="Lines Added">
                +412
              </span>
              <span className="font-mono text-red-600" title="Lines Removed">
                -385
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Hero() {
  return (
    <LazyMotion features={domAnimation}>
      <Container className="relative py-16">
        <div className="flex flex-col items-center justify-between gap-12 lg:flex-row lg:gap-20">
          <div className="w-full max-w-2xl">
            <p className="text-[#E6A91A] text-sm font-semibold tracking-wider uppercase">
              Codebase Reporting tool
            </p>
            <h1 className="text-foreground text-5xl/[1.1] tracking-tight text-balance sm:text-6xl/[1.1] md:text-7xl/[1.1]">
              <span className="decoration-[#E6A91A] underline decoration-4 underline-offset-4">
                Ship faster
              </span>{" "}
              with
              <span className="relative ml-4">
                <span className="text-highlight">automated code reports</span>
              </span>
            </h1>
            <p className="text-muted-foreground relative mt-6 max-w-xl text-xl/7 font-medium">
              Replace daily standups with AI-powered code summaries so your
              entire team stays aligned—wherever they work.
            </p>
            <div className="text-muted-foreground mt-4 space-y-4 text-lg">
              <div className="flex items-start gap-x-3">
                <span className="flex h-[1lh] items-center">
                  <CheckIcon className="text-[#E6A91A] size-5 shrink-0" />
                </span>
                <span>
                  <strong>Code reports</strong> that connect tech with business
                </span>
              </div>
              <div className="flex items-start gap-x-3">
                <span className="flex h-[1lh] items-center">
                  <CheckIcon className="text-[#E6A91A] size-5 shrink-0" />
                </span>
                <span>
                  Automated <strong>email digests</strong> & Slack notifications
                </span>
              </div>
              <div className="flex items-start gap-x-3">
                <span className="flex h-[1lh] items-center">
                  <CheckIcon className="text-[#E6A91A] size-5 shrink-0" />
                </span>
                <span>
                  Team <strong>velocity analytics</strong> and trends
                </span>
              </div>
              <div className="flex items-start gap-x-3">
                <span className="flex h-[1lh] items-center">
                  <CheckIcon className="text-[#E6A91A] size-5 shrink-0" />
                </span>
                <span>
                  <strong>Multi-user</strong> organization accounts
                </span>
              </div>
            </div>
            <div className="my-10">
              <div className="h-[52px]">
                <Link
                  href="/#pricing"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("pricing")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <div className="flex flex-col">
                    <Button size="lg" className="w-full sm:w-auto">
                      Connect GitHub & Subscribe{" "}
                      <ArrowRight className="ml-2 size-4" />
                    </Button>
                    <p className="text-muted-foreground mt-2 w-full text-center text-xs sm:w-auto">
                      30-DAY MONEY-BACK GUARANTEE
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
          <div className="flex max-w-2xl flex-col items-center justify-center">
            <svg
              width="109"
              height="24"
              viewBox="0 0 218 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-muted-foreground hidden w-24 lg:block"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.553886 43.6953C1.04659 43.2025 1.32814 43.1086 2.64201 43.1086C3.6978 43.1086 4.54243 43.2729 5.41053 43.6484C6.63055 44.1412 6.84171 44.1647 14.8657 44.1412C23.4294 44.1178 28.0514 43.9535 45.601 42.9913C68.8753 41.7006 81.0521 40.668 95.3404 38.7671C110.075 36.8193 130.745 32.6656 143.555 29.1219C152.447 26.6344 172.765 19.9696 180.601 16.9422C187.828 14.1496 196.72 9.92543 198.691 8.35309L199.277 7.88374L196.227 8.02454C190.197 8.30615 173.727 8.23576 173.187 7.93068C172.53 7.60213 171.944 6.49915 171.944 5.65432C171.944 4.66868 173.234 3.54224 174.759 3.23716C175.463 3.09635 179.264 2.86167 183.206 2.74433C195.101 2.36885 200.849 1.89949 207.723 0.749579C213.19 -0.165658 214.316 -0.21259 215.56 0.444503C217.085 1.2424 217.554 1.99337 217.648 3.87078C217.718 5.11457 217.624 5.84206 217.132 7.27359C216.311 9.69075 213.472 15.5811 209.694 22.6683C204.509 32.3839 204.791 31.9381 204.251 31.3983C203.665 30.8116 206.527 19.2655 209.272 11.1692C210.023 8.89285 210.656 6.96851 210.656 6.87464C210.656 6.80423 210.304 6.73382 209.882 6.73382C209.295 6.73382 208.709 7.08585 207.254 8.28269C199.136 14.9475 189.165 19.5706 167.721 26.6344C147.121 33.4165 138.98 35.599 121.266 39.0018C94.6131 44.1412 73.0281 46.136 33.0957 47.1451C24.4148 47.3563 20.3793 47.3563 15.0065 47.1216C4.16704 46.6757 1.11697 46.2768 0.319265 45.1738C-0.149975 44.4698 -0.126513 44.3524 0.553886 43.6953Z"
                fill="currentColor"
              />
            </svg>
            <ArrowBottom className="lg:hidden" />
          </div>
          <div className="w-full max-w-2xl">
            <Changelog />
          </div>
        </div>
      </Container>
    </LazyMotion>
  );
}
